import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// EXAMPLE DATA (all projects marked as Complete with enhanced Significant Findings)
const auditData = [
  {
    Ref: "M1",
    "Project Name": "Vendors Review",
    Region: "MENA",
    Country: "UAE",
    Function: "Supply Chain",
    "Project Code": "UAE/VR/2021/1",
    "Assignment type": "Main Audit",
    "Audit Classification": "Combined Audit",
    "Audit Project in-charge": "New Staff",
    "Consultants Involved": "No",
    "Overall Project Status": "Complete",
    "Current Stage": "Complete",
    "Total budget in Hours": null,
    "Date e-mail notification sent": "27-Oct-20",
    "Date of Kick-off Meeting": "27-Oct-20",
    "Delay in Kick-off Meeting": "-",
    "Date of Draft Report Issued": "30-Nov-20",
    "Days (Draft Report - Kick off)": 34,
    "Date of Closing Meeting": "13-Dec-20",
    "Days (Closing Meeting - Draft)": 13,
    "Final Report Issued Date": "28-Dec-20",
    "Days (Final Report - Closing Meeting)": 15,
    "Executive Summary Attached": "Yes",
    "Overall Report Rating": "Needs Improvement",
    "Follow-up Report Date": "",
    "Target Release Date as per Plan": "7-Jan-21",
    "Days Completion": 62,
    Month: 2.07,
    "Days completion w/o weekends": 46,
    "Total Working Month": 1.53,
    "Number Hours Spent": null,
    "Per Plan": "Original",
    "Assigned Auditor": "New Staff",
    "Complete in": "Q1",
    "Project Status vs Plan": "On-time",
    "Total Observations": 13,
    "Total High": 5,
    "Total Medium": 5,
    "Total Low": 3,
    "Total BP": 0,
    "Significant Findings":
      "Vendor compliance issues were identified. We strongly recommend a comprehensive revision of supply chain protocols and enhanced vendor monitoring. Investing in automated compliance systems will further mitigate risks and improve accountability.",
  },
  {
    Ref: "M2",
    "Project Name": "Financial Audit",
    Region: "Europe",
    Country: "UK",
    Function: "Finance",
    "Project Code": "UK/FA/2021/2",
    "Assignment type": "Main Audit",
    "Audit Classification": "Financial Audit",
    "Audit Project in-charge": "Senior Staff",
    "Consultants Involved": "Yes",
    "Overall Project Status": "Complete",
    "Current Stage": "Complete",
    "Total budget in Hours": 120,
    "Date e-mail notification sent": "15-Jan-21",
    "Date of Kick-off Meeting": "20-Jan-21",
    "Delay in Kick-off Meeting": "5 days",
    "Date of Draft Report Issued": "25-Feb-21",
    "Days (Draft Report - Kick off)": 35,
    "Date of Closing Meeting": "10-Mar-21",
    "Days (Closing Meeting - Draft)": 15,
    "Final Report Issued Date": "20-Mar-21",
    "Days (Final Report - Closing Meeting)": 10,
    "Executive Summary Attached": "Yes",
    "Overall Report Rating": "Satisfactory",
    "Follow-up Report Date": "",
    "Target Release Date as per Plan": "15-Mar-21",
    "Days Completion": 45,
    Month: 2,
    "Days completion w/o weekends": 40,
    "Total Working Month": 2,
    "Number Hours Spent": 60,
    "Per Plan": "Original",
    "Assigned Auditor": "Senior Staff",
    "Complete in": "Q2",
    "Project Status vs Plan": "On-time",
    "Total Observations": 7,
    "Total High": 2,
    "Total Medium": 3,
    "Total Low": 2,
    "Total BP": 0,
    "Significant Findings":
      "The financial audit was completed successfully, revealing a robust control environment with only minor discrepancies. We recommend enhancing automation in expense tracking and tightening internal controls to optimize financial accuracy.",
  },
  {
    Ref: "M3",
    "Project Name": "Operations Review",
    Region: "APAC",
    Country: "Singapore",
    Function: "Operations",
    "Project Code": "SG/OR/2021/3",
    "Assignment type": "Follow-up",
    "Audit Classification": "Combined Audit",
    "Audit Project in-charge": "Staff",
    "Consultants Involved": "No",
    "Overall Project Status": "Complete",
    "Current Stage": "Complete",
    "Total budget in Hours": 100,
    "Date e-mail notification sent": "01-Feb-21",
    "Date of Kick-off Meeting": "05-Feb-21",
    "Delay in Kick-off Meeting": "4 days",
    "Date of Draft Report Issued": "20-Feb-21",
    "Days (Draft Report - Kick off)": 15,
    "Date of Closing Meeting": "25-Feb-21",
    "Days (Closing Meeting - Draft)": 5,
    "Final Report Issued Date": "05-Mar-21",
    "Days (Final Report - Closing Meeting)": 8,
    "Executive Summary Attached": "Yes",
    "Overall Report Rating": "Satisfactory",
    "Follow-up Report Date": "",
    "Target Release Date as per Plan": "10-Mar-21",
    "Days Completion": 30,
    Month: 1,
    "Days completion w/o weekends": 22,
    "Total Working Month": 1,
    "Number Hours Spent": 95,
    "Per Plan": "Original",
    "Assigned Auditor": "Staff",
    "Complete in": "Q1",
    "Project Status vs Plan": "On-time",
    "Total Observations": 10,
    "Total High": 3,
    "Total Medium": 4,
    "Total Low": 3,
    "Total BP": 1,
    "Significant Findings":
      "The operations review yielded critical insights into process inefficiencies. We recommend a strategic overhaul of operational workflows and the implementation of targeted training initiatives to drive efficiency, reduce costs, and boost productivity.",
  },
];

// Helper function to sum a field across the data array.
const sumField = (data, field) => {
  return data.reduce((acc, cur) => acc + (cur[field] || 0), 0);
};

// Options for the Doughnut chart with labels inside.
const doughnutOptions = {
  plugins: {
    datalabels: {
      color: "#fff",
      anchor: "center",
      align: "center",
      formatter: (value) => value,
    },
    legend: {
      display: true,
      position: "bottom",
    },
  },
};

// Options for the timeline (horizontal Bar) chart with labels inside.
const timelineOptions = {
  indexAxis: "y",
  scales: {
    x: { beginAtZero: true },
  },
  plugins: {
    datalabels: {
      color: "black",
      anchor: "end",
      align: "end",
      formatter: (value) => value,
    },
    legend: { display: false },
  },
};

const ProjectDashboard = () => {
  // Aggregated summary for all projects.
  const totalProjects = auditData.length;
  const totalObservations = sumField(auditData, "Total Observations");
  const totalHigh = sumField(auditData, "Total High");
  const totalMedium = sumField(auditData, "Total Medium");
  const totalLow = sumField(auditData, "Total Low");
  const totalBP = sumField(auditData, "Total BP");

  // State for selected project filter.
  const [selectedProjectRef, setSelectedProjectRef] = useState(auditData[0].Ref);

  // Find the selected project data.
  const selectedProject = auditData.find(
    (project) => project.Ref === selectedProjectRef
  );

  // Data for selected project's Observations Breakdown (Doughnut chart).
  const projectObsData = {
    labels: ["High", "Medium", "Low", "Best Practices"],
    datasets: [
      {
        label: "Observations Breakdown",
        data: [
          selectedProject["Total High"],
          selectedProject["Total Medium"],
          selectedProject["Total Low"],
          selectedProject["Total BP"],
        ],
        backgroundColor: [
          "rgba(255, 99, 132, 0.7)",
          "rgba(54, 162, 235, 0.7)",
          "rgba(255, 206, 86, 0.7)",
          "rgba(75, 192, 192, 0.7)",
        ],
      },
    ],
  };

  // Data for selected project's timeline chart (Gantt style).
  const projectTimelineData = {
    labels: [
      "Draft Report - Kick-off",
      "Closing Meeting - Draft",
      "Final Report - Closing Meeting",
    ],
    datasets: [
      {
        label: "Days",
        data: [
          selectedProject["Days (Draft Report - Kick off)"],
          selectedProject["Days (Closing Meeting - Draft)"],
          selectedProject["Days (Final Report - Closing Meeting)"],
        ],
        backgroundColor: [
          "rgba(153, 102, 255, 0.7)",
          "rgba(255, 159, 64, 0.7)",
          "rgba(255, 99, 132, 0.7)",
        ],
      },
    ],
  };

  return (
    <Container maxWidth="lg" sx={{ my: 4 }}>
      <Typography variant="h4" gutterBottom>
        Audit Dashboard Quarterly Summary
      </Typography>

      {/* Summary Cards for All Projects */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6">Total Projects</Typography>
              <Typography variant="h4">{totalProjects}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6">
                Total Observations All Projects
              </Typography>
              <Typography variant="h4">{totalObservations}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Observations by Rating
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1">High</Typography>
                  <Typography variant="h6">{totalHigh}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1">Medium</Typography>
                  <Typography variant="h6">{totalMedium}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1">Low</Typography>
                  <Typography variant="h6">{totalLow}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1">Best Practices</Typography>
                  <Typography variant="h6">{totalBP}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter: Select a Project */}
      <Box sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="project-select-label">Select Project</InputLabel>
          <Select
            labelId="project-select-label"
            value={selectedProjectRef}
            label="Select Project"
            onChange={(e) => setSelectedProjectRef(e.target.value)}
          >
            {auditData.map((project) => (
              <MenuItem key={project.Ref} value={project.Ref}>
                {project["Project Name"]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Charts for Selected Project */}
      <Typography variant="h5" gutterBottom>
        {selectedProject["Project Name"]} - Detailed Metrics
      </Typography>
      <Grid container spacing={4}>
        {/* Observations Breakdown Chart */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              p: 2,
              backgroundColor: "#fff",
              height: "100%",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Observations Breakdown
            </Typography>
            <Doughnut data={projectObsData} options={doughnutOptions} />
          </Box>
        </Grid>
        {/* Project Timeline Chart */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              p: 2,
              backgroundColor: "#fff",
              height: "100%",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Project Timeline (Days)
            </Typography>
            <Bar data={projectTimelineData} options={timelineOptions} />
          </Box>
        </Grid>
      </Grid>

      {/* Significant Findings Section */}
      <Box sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Significant Findings Noted
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {selectedProject["Significant Findings"]}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Recommendations:
            </Typography>
            <Typography variant="body2">
              {selectedProject.Ref === "M1" &&
                "Implement strict vendor monitoring and conduct regular compliance audits. Consider investing in automated systems for enhanced oversight and risk mitigation."}
              {selectedProject.Ref === "M2" &&
                "Enhance automation for expense tracking and tighten internal controls. Regular reviews and continuous monitoring will further optimize financial accuracy."}
              {selectedProject.Ref === "M3" &&
                "Streamline operational workflows and implement targeted training programs. A strategic review of processes will drive efficiency and reduce operational costs."}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ProjectDashboard;
